declare global {
  interface Window {
    dataLayer: any[];
    _isGtmLoaded: boolean;
    gtag: (...args: any[]) => void;
  }
}

type Me = {
  id: string;
  name: string;
  permission: string;
  status: string;
};

type Profile = {
  id: string;
  name: string;
  phone?: {
    phone?: string;
  };
  email?: {
    email: string;
  };
};

type ConsentValue = 'granted' | 'denied';

export type TConsentMode = {
  necessary: boolean;
  ad_storage: boolean;
  ad_user_data: boolean;
  ad_personalization: boolean;
  analytics_storage: boolean;
};

type PushConsentMode = 'default' | 'update';

const DEFAULT_CONSENT: TConsentMode = {
  necessary: false,
  ad_storage: false,
  ad_user_data: false,
  ad_personalization: false,
  analytics_storage: false,
};

const toConsentValue = (value: boolean): ConsentValue => (value ? 'granted' : 'denied');

const normalizeConsent = (input: unknown): Partial<TConsentMode> | undefined => {
  if (!input || typeof input !== 'object') return undefined;

  const record = input as Record<string, unknown>;
  const normalized: Partial<TConsentMode> = {};

  for (const key of Object.keys(record)) {
    if (!(key in DEFAULT_CONSENT)) continue;

    const value = record[key];
    if (value === 'granted' || value === true) normalized[key as keyof TConsentMode] = true;
    if (value === 'denied' || value === false) normalized[key as keyof TConsentMode] = false;
  }

  return Object.keys(normalized).length > 0 ? normalized : undefined;
};

const pushConsent = (mode: PushConsentMode, consent?: Partial<TConsentMode>) => {
  if (typeof window === 'undefined') return;

  const finalConsent: TConsentMode = { ...DEFAULT_CONSENT, ...(consent || {}) };
  const gtmConsent = {
    necessary: toConsentValue(finalConsent.necessary),
    ad_storage: toConsentValue(finalConsent.ad_storage),
    ad_user_data: toConsentValue(finalConsent.ad_user_data),
    ad_personalization: toConsentValue(finalConsent.ad_personalization),
    analytics_storage: toConsentValue(finalConsent.analytics_storage),
  };

  window.dataLayer = window.dataLayer || [];

  function gtag() {
    window.dataLayer.push(arguments);
  }

  //@ts-ignore
  gtag('consent', mode, gtmConsent);

  if (mode === 'update') {
    try {
      localStorage.setItem('consentMode', JSON.stringify(gtmConsent));
    } catch {}
  }
};

const getStoredConsent = () => {
  try {
    const storedConsent = localStorage.getItem('consentMode');
    if (!storedConsent) return undefined;

    const parsedConsent = JSON.parse(storedConsent);
    return normalizeConsent(parsedConsent);
  } catch {}

  return undefined;
};

const getConsentStatus = (): TConsentMode => {
  if (typeof window === 'undefined') {
    return { ...DEFAULT_CONSENT };
  }

  const storedConsent = getStoredConsent();
  if (!storedConsent) {
    return { ...DEFAULT_CONSENT };
  }

  return { ...DEFAULT_CONSENT, ...storedConsent };
};

type TagManagerType = {
  initialize: (args: { gtmId: string }) => void;
};

const eventQueue: Array<{ event: string; data?: Record<string, any> }> = [];

const waitForGTMLoaded = async () => {
  if (typeof window === 'undefined') {
    return false;
  }

  if (window._isGtmLoaded === true) {
    return true;
  }

  const checkPromise = new Promise((resolve) => {
    if (window.dataLayer.some((item) => item.event === 'gtm.load')) {
      window._isGtmLoaded = true;

      while (eventQueue.length) {
        const event = eventQueue.shift();
        if (event) pushToDataLayer(event.event, event.data);
      }

      resolve(true);
      return;
    }

    const intervalId = setInterval(() => {
      if (window.dataLayer.some((item) => item.event === 'gtm.load')) {
        clearInterval(intervalId);
        window._isGtmLoaded = true;

        while (eventQueue.length) {
          const event = eventQueue.shift();

          if (event) pushToDataLayer(event.event, event.data);
        }

        resolve(true);
      }
    }, 500);

    window.addEventListener('beforeunload', () => clearInterval(intervalId), { once: true });
  });

  const timeoutPromise = new Promise((resolve) => {
    setTimeout(() => {
      resolve(false);
    }, 30000);
  });

  return await Promise.race([checkPromise, timeoutPromise]);
};

const initializeGtm = async (gtmId?: string) => {
  if (typeof window === 'undefined') return;

  if (!gtmId || window?._isGtmLoaded) return;

  const TagManager = (await import('react-gtm-module')).default as unknown as TagManagerType;

  setTimeout(() => {
    pushConsent('default', getStoredConsent());
    TagManager.initialize({ gtmId });

    waitForGTMLoaded();
  }, 3000);
};

const pushToDataLayer = (event: string, data?: Record<string, any>) => {
  if (typeof window === 'undefined') return;

  if (!window._isGtmLoaded) {
    eventQueue.push({ event, data });
    return;
  }

  if (!window.dataLayer) {
    window.dataLayer = window.dataLayer || [];
  }

  window.dataLayer.push({
    event,
    ...data,
  });
};

const pushUserToDataLayer = (event: string, me?: Me, profile?: Profile) => {
  const user: Record<string, any> = {};

  if (me) {
    user.userId = me.id;
    user.userName = me.name;
  }

  if (profile) {
    user.profileId = profile.id;
    user.profileName = profile.name;
    user.profilePhone = profile.phone?.phone;
    user.profileEmail = profile.email?.email;
  }

  pushToDataLayer(event, user);
};

export { initializeGtm, pushToDataLayer, pushUserToDataLayer, pushConsent, getConsentStatus };
